/**
 * TurboClaim Unified API Client
 * 
 * This is a convenience wrapper that combines all domain-specific API clients
 * into a single unified client instance for easier usage.
 */

import {
  Configuration,
  AnalyticsApi,
  ApiKeysApi,
  AutomationsApi,
  BillingApi,
  ClaimsApi,
  ClaimFoldersApi,
  ClaimPipelinesApi,
  ClaimPhotosApi,
  ClaimLinksApi,
  CompaniesApi,
  ComparisonsApi,
  ContactsApi,
  DocumentsApi,
  EstimatesApi,
  FinancialsApi,
  CompanyFinancialsApi,
  FormTemplatesApi,
  GmailApi,
  OrganizationsApi,
  PropertiesApi,
  SupplementsApi,
  UsersApi,
  ActivitiesApi,
  ChatApi,
  OnboardingApi,
  NotificationsApi,
  TemplatesApi,
  TasksApi,
  NotesApi,
  SSEApi,
  type Middleware
} from './generated/client/index';

export interface TurboClaimClientConfig {
  basePath?: string;
  accessToken?: string | (() => string | Promise<string>);
  headers?: Record<string, string>;
  middleware?: Middleware[];
}

/**
 * Unified TurboClaim API Client
 * 
 * Provides a single client interface with all API methods organized by domain.
 */
export class TurboClaimClient {
  private config: Configuration;
  private _basePath: string;
  private _accessToken: string | (() => string | Promise<string>) | undefined;

  public readonly analytics: AnalyticsApi;
  public readonly apiKeys: ApiKeysApi;
  public readonly automations: AutomationsApi;
  public readonly billing: BillingApi;
  public readonly claims: ClaimsApi;
  public readonly claimWidgets: ClaimsApi;
  public readonly claimFolders: ClaimFoldersApi;
  public readonly claimPipelines: ClaimPipelinesApi;
  public readonly claimPhotos: ClaimPhotosApi;
  public readonly claimLinks: ClaimLinksApi;
  public readonly companies: CompaniesApi;
  public readonly comparisons: ComparisonsApi;
  public readonly contacts: ContactsApi;
  public readonly documents: DocumentsApi;
  public readonly estimates: EstimatesApi;
  public readonly financials: FinancialsApi;
  public readonly companyFinancials: CompanyFinancialsApi;
  public readonly formTemplates: FormTemplatesApi;
  public readonly gmail: GmailApi;
  public readonly organizations: OrganizationsApi;
  public readonly properties: PropertiesApi;
  public readonly supplements: SupplementsApi;
  public readonly users: UsersApi;
  public readonly activities: ActivitiesApi;
  public readonly chat: ChatApi;
  public readonly onboarding: OnboardingApi;
  public readonly notifications: NotificationsApi;
  public readonly templates: TemplatesApi;
  public readonly tasks: TasksApi;
  public readonly notes: NotesApi;
  public readonly sse: SSEApi;

  constructor(config: TurboClaimClientConfig = {}) {
    this._basePath = config.basePath || 'http://localhost:3000';
    this._accessToken = config.accessToken;

    // Build configuration
    const configOptions: {
      basePath: string;
      accessToken?: string | (() => string | Promise<string>);
      headers?: Record<string, string>;
      middleware?: Middleware[];
    } = {
      basePath: this._basePath,
    };

    // Add authentication if provided
    if (this._accessToken) {
      configOptions.accessToken = this._accessToken;
    }

    // Add custom headers if provided
    if (config.headers) {
      configOptions.headers = config.headers;
    }

    // Add middleware if provided
    if (config.middleware) {
      configOptions.middleware = config.middleware;
    }

    this.config = new Configuration(configOptions);

    // Initialize all API clients
    this.analytics = new AnalyticsApi(this.config);
    this.apiKeys = new ApiKeysApi(this.config);
    this.automations = new AutomationsApi(this.config);
    this.billing = new BillingApi(this.config);
    this.claims = new ClaimsApi(this.config);
    this.claimWidgets = new ClaimsApi(this.config);
    this.claimFolders = new ClaimFoldersApi(this.config);
    this.claimPipelines = new ClaimPipelinesApi(this.config);
    this.claimPhotos = new ClaimPhotosApi(this.config);
    this.claimLinks = new ClaimLinksApi(this.config);
    this.companies = new CompaniesApi(this.config);
    this.comparisons = new ComparisonsApi(this.config);
    this.contacts = new ContactsApi(this.config);
    this.documents = new DocumentsApi(this.config);
    this.estimates = new EstimatesApi(this.config);
    this.financials = new FinancialsApi(this.config);
    this.companyFinancials = new CompanyFinancialsApi(this.config);
    this.formTemplates = new FormTemplatesApi(this.config);
    this.gmail = new GmailApi(this.config);
    this.organizations = new OrganizationsApi(this.config);
    this.properties = new PropertiesApi(this.config);
    this.supplements = new SupplementsApi(this.config);
    this.users = new UsersApi(this.config);
    this.activities = new ActivitiesApi(this.config);
    this.chat = new ChatApi(this.config);
    this.onboarding = new OnboardingApi(this.config);
    this.notifications = new NotificationsApi(this.config);
    this.templates = new TemplatesApi(this.config);
    this.tasks = new TasksApi(this.config);
    this.notes = new NotesApi(this.config);
    this.sse = new SSEApi(this.config);
  }

  /**
   * Update the access token for all API clients
   */
  setAccessToken(token: string | (() => string | Promise<string>)): void {
    this._accessToken = token;

    this.config = new Configuration({
      basePath: this._basePath,
      accessToken: this._accessToken,
    });

    // Re-initialize all clients with new config
    this.reinitializeClients();
  }

  /**
   * Update the base path for all API clients
   */
  setBasePath(basePath: string): void {
    this._basePath = basePath;

    this.config = new Configuration({
      basePath: this._basePath,
      accessToken: this._accessToken,
    });

    // Re-initialize all clients with new config
    this.reinitializeClients();
  }

  private reinitializeClients() {
    Object.assign(this, {
      analytics: new AnalyticsApi(this.config),
      apiKeys: new ApiKeysApi(this.config),
      automations: new AutomationsApi(this.config),
      activities: new ActivitiesApi(this.config),
      chat: new ChatApi(this.config),
      onboarding: new OnboardingApi(this.config),
      billing: new BillingApi(this.config),
      claims: new ClaimsApi(this.config),
      claimWidgets: new ClaimsApi(this.config),
      claimFolders: new ClaimFoldersApi(this.config),
      claimPipelines: new ClaimPipelinesApi(this.config),
      claimPhotos: new ClaimPhotosApi(this.config),
      claimLinks: new ClaimLinksApi(this.config),
      companies: new CompaniesApi(this.config),
      comparisons: new ComparisonsApi(this.config),
      contacts: new ContactsApi(this.config),
      documents: new DocumentsApi(this.config),
      estimates: new EstimatesApi(this.config),
      financials: new FinancialsApi(this.config),
      companyFinancials: new CompanyFinancialsApi(this.config),
      formTemplates: new FormTemplatesApi(this.config),
      gmail: new GmailApi(this.config),
      organizations: new OrganizationsApi(this.config),
      properties: new PropertiesApi(this.config),
      supplements: new SupplementsApi(this.config),
      users: new UsersApi(this.config),
      notifications: new NotificationsApi(this.config),
      templates: new TemplatesApi(this.config),
      tasks: new TasksApi(this.config),
      notes: new NotesApi(this.config),
      sse: new SSEApi(this.config),
    });
  }
}

// Export the generated types and clients for advanced usage
export * from './generated/client/index';

// Export the unified client as default
export default TurboClaimClient;
